import { api, type DefaultChannel } from "@/lib/api";
import { choiceFromCurrent, sameChoice, type DefaultChannelChoice } from "@/lib/default-channel";

/**
 * Persist a choice: create a staged channel first, then PUT it as the default;
 * DELETE for "no default channel". Skips the call when nothing changed, so
 * "keep it as is" never needs the Boo. Resolves to the saved choice, with a
 * created channel as a regular channel. `onCreated` fires as soon as the
 * channel exists, so a failed PUT retries against it instead of creating it
 * again.
 */
export async function saveDefaultChannelChoice(
  containerId: string,
  choice: DefaultChannelChoice,
  current: DefaultChannel | null | undefined,
  opts: { onCreated?: (created: DefaultChannelChoice) => void } = {},
): Promise<DefaultChannelChoice> {
  if (sameChoice(choice, choiceFromCurrent(current))) return choice;
  if (choice.kind === "none") {
    await api.containers.channels.defaultChannel.clear(containerId);
    return choice;
  }
  let target = choice;
  if (target.kind === "new") {
    const created = await api.containers.channels.create(containerId, {
      platform: target.platform,
      name: target.name,
      isPrivate: target.isPrivate,
    });
    target = {
      kind: "channel",
      platform: target.platform,
      channelOrSpaceId: created.channelOrSpaceId,
      displayName: created.displayName,
      surfaceType: created.surfaceType,
    };
    opts.onCreated?.(target);
  }
  await api.containers.channels.defaultChannel.set(containerId, {
    platform: target.platform,
    channelOrSpaceId: target.channelOrSpaceId,
    displayName: target.displayName ?? undefined,
  });
  return target;
}
